import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import { QueryKeys, SURVEY_ID_HEADER } from '../constants';
import { MissingIdError, useFetch } from './utils';

export function useUpdateThemeSummary() {
  const fetcher = useFetch();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ codeFrameId, themeId, surveyId, data }) =>
      fetcher({
        path: `v1/codeframes/${codeFrameId}/themes/${themeId}/update-summary/`,
        method: 'PATCH',
        data: JSON.stringify(data),
        headers: { [SURVEY_ID_HEADER]: surveyId },
      }),
    onSettled: (_data, _error, { themeId, codeFrameId, surveyId }) => {
      queryClient.invalidateQueries([QueryKeys.THEME_SUMMARY, { themeId, codeFrameId, surveyId }]);
    },
    onError: () => {
      message.error('Something went wrong updating theme summary');
    },
  });
}

export function useUpdateQuestionSummary() {
  const fetcher = useFetch();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ codeFrameId, surveyId, data }) => {
      if (!surveyId) {
        throw new MissingIdError('surveyId');
      }
      return fetcher({
        path: `v1/codeframes/${codeFrameId}/update-summary/`,
        method: 'PATCH',
        data: JSON.stringify(data),
      });
    },
    onSettled: (_data, _error, { codeFrameId, surveyId }) => {
      queryClient.invalidateQueries([QueryKeys.QUESTION_SUMMARY, { codeFrameId, surveyId }]);
    },
    onError: () => {
      message.error('Something went wrong updating question summary');
    },
  });
}

export function useThemeSummary({ codeFrameId, themeId, surveyId }, options = {}) {
  const fetcher = useFetch();
  return useQuery({
    queryKey: [QueryKeys.THEME_SUMMARY, { codeFrameId, themeId, surveyId }],
    queryFn: () =>
      fetcher({
        path: `v1/codeframes/${codeFrameId}/themes/${themeId}/summary/`,
        headers: { [SURVEY_ID_HEADER]: surveyId },
      }),
    ...options,
  });
}

export function useQuestionSummary({ codeFrameId, surveyId }, options = {}) {
  const fetcher = useFetch();
  return useQuery({
    queryKey: [QueryKeys.QUESTION_SUMMARY, { codeFrameId, surveyId }],
    queryFn: () =>
      fetcher({
        path: `v1/codeframes/${codeFrameId}/summary/`,
        headers: { [SURVEY_ID_HEADER]: surveyId },
      }),
    ...options,
  });
}

export function useSegmentSummary({ codeFrameId, segmentId, surveyId }, options = {}) {
  const fetcher = useFetch();
  return useQuery({
    queryKey: [QueryKeys.SEGMENT_SUMMARY, { codeFrameId, segmentId, surveyId }],
    queryFn: () =>
      fetcher({
        path: `v1/codeframes/${codeFrameId}/segments/${segmentId}/summary/`,
        headers: { [SURVEY_ID_HEADER]: surveyId },
      }),
    ...options,
  });
}

export function useSegmentSummaryTheme(
  { codeFrameId, segmentId, themeId, surveyId },
  options = {},
) {
  const fetcher = useFetch();
  return useQuery({
    queryKey: [QueryKeys.SEGMENT_SUMMARY_THEME, { codeFrameId, segmentId, themeId, surveyId }],
    queryFn: () =>
      fetcher({
        path: `v1/codeframes/${codeFrameId}/segments/${segmentId}/themes/${themeId}/summary/`,
        headers: { [SURVEY_ID_HEADER]: surveyId },
      }),
    ...options,
  });
}

export function useCodeFrameResponses({ codeFrameId, filters }, options = {}) {
  const fetcher = useFetch();
  return useQuery({
    queryKey: [QueryKeys.CODE_FRAME_RESPONSES, { codeFrameId, filters }],
    queryFn: () =>
      fetcher({
        path: `v1/codeframes/${codeFrameId}/responses/`,
        filters,
      }),
    ...options,
  });
}

export function useSuggestedThemes({ codeFrameId }) {
  const fetcher = useFetch();
  return useQuery({
    queryKey: [QueryKeys.SUGGESTED_THEMES, { codeFrameId }],
    queryFn: () =>
      fetcher({
        path: `v1/codeframes/${codeFrameId}/suggested-themes/`,
      }),
  });
}

export function useCodeFrameThemes({ codeFrameId }) {
  const fetcher = useFetch();
  return useQuery({
    queryKey: [QueryKeys.CODE_FRAME_THEMES, { codeFrameId }],
    queryFn: () =>
      fetcher({
        path: `v1/codeframes/${codeFrameId}/themes/`,
      }),
    // if theme is a parent set response_ids to all unique child response ids
    select: (data) => ({
      ...data,
      data: data.data.map((theme) =>
        theme.is_parent
          ? {
              ...theme,
              response_ids: [
                ...new Set(
                  data.data
                    .filter((t) => t.parent_theme_id === theme.id)
                    .flatMap((t) => t.response_ids),
                ),
              ],
            }
          : theme,
      ),
    }),
  });
}

export function useGroupThemes() {
  const fetcher = useFetch();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ codeFrameId, data }) =>
      fetcher({
        path: `v1/codeframes/${codeFrameId}/group-themes/`,
        method: 'PUT',
        data: JSON.stringify(data),
      }),
    onSettled: (_data, _error, { codeFrameId }) => {
      queryClient.invalidateQueries([QueryKeys.CODE_FRAME_THEMES, { codeFrameId }]);
      queryClient.invalidateQueries([QueryKeys.THEME_COVERAGE, { codeFrameId }]);
    },
    onError: () => {
      message.error('Something went wrong creating or updating theme');
    },
  });
}

export function useOmitTheme() {
  const fetcher = useFetch();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ themeId, codeFrameId, data }) =>
      fetcher({
        path: `v1/codeframes/${codeFrameId}/themes/${themeId}/omit/`,
        method: 'PUT',
        data: JSON.stringify(data),
      }),
    onSettled: (_data, _error, { codeFrameId }) =>
      queryClient.invalidateQueries([QueryKeys.CODE_FRAME_THEMES, { codeFrameId }]),
    onError: () => {
      message.error('Something went wrong omitting theme');
    },
  });
}

export function useThemeCoverage({ codeFrameId }, options = {}) {
  const fetcher = useFetch();
  return useQuery({
    queryKey: [QueryKeys.THEME_COVERAGE, { codeFrameId }],
    queryFn: () => fetcher({ path: `v1/codeframes/${codeFrameId}/theme-coverage/` }),
    ...options,
  });
}
